import type { AvailabilityResponse } from "@/shared";
import type { OnlineSchedulingInsuranceCardResponse } from "@/src/lib/scheduling/insurance-card-extraction";
import type {
  OnlineSchedulingAvailabilityRequest,
  OnlineSchedulingPatientOptionsRequest,
  OnlineSchedulingEligibilityResponse,
  OnlineSchedulingBookRequest,
  OnlineSchedulingBookResponse,
  OnlineSchedulingOptionsResponse,
} from "@/src/lib/scheduling/online-scheduling-schemas";

/**
 * Browser-side fetch helpers for the online scheduling API. These import types only (no server
 * modules), so they are safe to use from client components.
 */

const BASE = "/api/online-scheduling";

function withTenant(path: string, tenant?: string | null): string {
  return tenant ? `${path}${path.includes("?") ? "&" : "?"}tenant=${encodeURIComponent(tenant)}` : path;
}

/** Applies the optional `?tenant` override and surfaces the API's error message. */
async function fetchJson<T>(path: string, tenant: string | null | undefined, init?: RequestInit): Promise<T> {
  const response = await fetch(withTenant(`${BASE}${path}`, tenant), init);

  if (!response.ok) {
    let message = `Request failed (${response.status})`;

    try {
      const data: unknown = await response.json();

      if (data && typeof data === "object" && !Array.isArray(data)) {
        const errData = data as { error?: string; message?: string };

        message = errData.error ?? errData.message ?? message;
      }
    } catch {
      // response had no JSON body; keep the status-based message
    }

    throw new Error(message);
  }

  return (await response.json()) as T;
}

export async function fetchOnlineSchedulingOptions(
  tenant?: string | null,
  regionCode?: string | null
): Promise<OnlineSchedulingOptionsResponse> {
  const path = regionCode ? `/options?regionCode=${encodeURIComponent(regionCode)}` : "/options";

  return fetchJson<OnlineSchedulingOptionsResponse>(path, tenant, { method: "GET" });
}

export async function fetchOnlineSchedulingAvailability(
  query: OnlineSchedulingAvailabilityRequest,
  tenant?: string | null
): Promise<AvailabilityResponse> {
  return fetchJson<AvailabilityResponse>("/availability", tenant, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(query),
  });
}

export async function bookOnlineSchedulingAppointment(
  request: OnlineSchedulingBookRequest,
  tenant?: string | null
): Promise<OnlineSchedulingBookResponse> {
  return fetchJson<OnlineSchedulingBookResponse>("/book", tenant, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
}

export async function checkOnlineSchedulingEligibility(
  request: OnlineSchedulingPatientOptionsRequest,
  tenant?: string | null
): Promise<OnlineSchedulingEligibilityResponse> {
  return fetchJson<OnlineSchedulingEligibilityResponse>("/eligibility", tenant, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
}

export async function readOnlineSchedulingInsuranceCard(
  files: File[],
  tenant?: string | null
): Promise<OnlineSchedulingInsuranceCardResponse> {
  const form = new FormData();

  for (const file of files) form.append("images", file);

  return fetchJson<OnlineSchedulingInsuranceCardResponse>("/insurance-card", tenant, {
    method: "POST",
    body: form,
    signal: AbortSignal.timeout(30_000),
  });
}
